/* eslint-disable capitalized-comments*/
import axios, { AxiosRequestConfig } from 'axios';
import mergeObjects from '~/utils/mergeObjects';
import trimSlashes from '~/utils/trimSlashes';

class BaseAPIClient {
    protected baseUrl: string;
    protected defaultHeaders: RequestInit['headers'];

    constructor(baseUrl: string, headers: RequestInit['headers']) {
        this.baseUrl = baseUrl;
        this.defaultHeaders = mergeObjects(
            {
                accept: 'application/json',
                'content-type': 'application/json',
            },
            headers
        );
    }

    async request<T>(
        config: Pick<AxiosRequestConfig, 'baseURL' | 'url' | 'method' | 'data' | 'params' | 'headers'>
    ): Promise<T> {
        const baseUrl = config.baseURL ?? this.baseUrl;
        let fullUrl = trimSlashes(`${baseUrl}${config.url}`);
        if (!fullUrl.startsWith('http')) {
            fullUrl = `/${fullUrl}`;
        }
        const isFormData = config.data instanceof FormData;
        if (isFormData) {
            return axios.request(config);
        }
        const fetchRequestConfig: RequestInit = {
            method: config.method,
            headers: isFormData
                ? mergeObjects({ 'content-type': 'multipart/form-data' }, config.headers)
                : mergeObjects(this.defaultHeaders, config.headers),
        };

        if (config.data) {
            if (isFormData) {
                fetchRequestConfig.body = config.data;
            } else {
                fetchRequestConfig.body = JSON.stringify(config.data);
            }
        }

        if (config.params) {
            fullUrl += `?${new URLSearchParams(config.params).toString()}`;
        }

        // Why we need to convert axios config into fetch config and use fetch here?
        const response = await fetch(fullUrl, fetchRequestConfig);

        // status 404 or 500 will set ok to false
        try {
            if (response.ok) {
                // Check if the response status is 204 (No Content) or if the content-length header is 0.
                // This is necessary because calling response.json() on an empty response body will cause an error.
                // Returning null in these cases prevents the error and allows the function to handle empty responses gracefully.
                if (response.status === 204 || response.headers.get('content-length') === '0') {
                    return null as T;
                }

                const contentType = response.headers.get('content-type');
                if (contentType?.includes('application/octet-stream') || contentType?.includes('application/pdf')) {
                    return response as T;
                }

                return await response.json();
            } else {
                throw response;
            }
        } catch {
            throw response;
        }
    }
}

export default BaseAPIClient;
